import {
  isSystemLockedReadOnlyAttributeKey,
  overlayEntryForCardEntity,
  type OverlayCardEntityKind,
} from "src/overlayAttributes/overlayAttributeCatalog";
import type {
  AttributeCatalogEntry,
  AttributeDisplayPolicy,
  AttributeEntityType,
  AttributePolicyByKey,
} from "./attributeDefaultsTypes";
import {
  DEFAULT_ATTRIBUTE_POLICY,
  overlayAttributeKey,
  policyFromFlags,
  type AttributePolicyFlags,
} from "./attributeDefaultsTypes";

const toConstructedPolicy = (
  value: AttributeDisplayPolicy | Partial<AttributePolicyFlags> | undefined
): AttributeDisplayPolicy | undefined => {
  if (!value) {
    return undefined;
  }
  if (
    "kind" in value &&
    (value.kind === "hidden" ||
      value.kind === "readOnly" ||
      value.kind === "editable")
  ) {
    return value;
  }
  return policyFromFlags(value);
};

const HIDDEN_POLICY: AttributeDisplayPolicy = { kind: "hidden" };

const READ_ONLY_POLICY: AttributeDisplayPolicy = {
  kind: "readOnly",
  defaultOnCards: false,
};

const DEFAULT_SHOW_READ_ONLY_ATTRIBUTE_KEYS = [
  "address",
  "city",
  "company",
  "country",
  "state",
  "timezone",
  "fte",
  "position.location",
  "position.fte",
] as const;

const DEFAULT_SHOW_READ_ONLY_KEY_SET = new Set<string>(
  DEFAULT_SHOW_READ_ONLY_ATTRIBUTE_KEYS
);

export const getDefaultAttributePolicies = (
  catalog: readonly AttributeCatalogEntry[]
): AttributePolicyByKey => {
  const policies: AttributePolicyByKey = {};

  for (const entry of catalog) {
    const key = overlayAttributeKey(entry.key);
    if (!key) {
      continue;
    }
    if (entry.internal) {
      policies[key] = HIDDEN_POLICY;
      continue;
    }
    if (DEFAULT_SHOW_READ_ONLY_KEY_SET.has(entry.key)) {
      policies[key] = READ_ONLY_POLICY;
    }
  }

  return policies;
};

type OverlayPolicyCatalogHint = Pick<
  AttributeCatalogEntry,
  "internal" | "systemReadOnly"
>;

export const getSeededDefaultPolicy = (
  key: string,
  entry?: OverlayPolicyCatalogHint
): AttributeDisplayPolicy => {
  if (entry?.internal) {
    return HIDDEN_POLICY;
  }
  if (DEFAULT_SHOW_READ_ONLY_KEY_SET.has(key)) {
    return READ_ONLY_POLICY;
  }
  return DEFAULT_ATTRIBUTE_POLICY;
};

export const getAttributePolicy = (
  policies: AttributePolicyByKey,
  key: string,
  entry?: OverlayPolicyCatalogHint
): AttributeDisplayPolicy => {
  const seeded = getSeededDefaultPolicy(key, entry);
  const branded = overlayAttributeKey(key);
  const stored = branded ? toConstructedPolicy(policies[branded]) : undefined;
  const merged = stored ?? seeded;
  if (entry?.systemReadOnly || isSystemLockedReadOnlyAttributeKey(key)) {
    if (merged.kind === "hidden") {
      return merged;
    }
    return { kind: "readOnly", defaultOnCards: merged.defaultOnCards };
  }
  return merged;
};

export const isAttributeHiddenFromOrgChart = (
  policy: AttributeDisplayPolicy
): boolean => policy.kind === "hidden";

export const isAttributeReadOnlyInOrgChart = (
  policy: AttributeDisplayPolicy
): boolean => policy.kind === "readOnly";

export const filterCatalogForPropertiesPicker = (
  catalog: readonly AttributeCatalogEntry[],
  policies: AttributePolicyByKey
): AttributeCatalogEntry[] =>
  catalog.filter(
    (entry) =>
      !entry.pickerExcluded &&
      !isAttributeHiddenFromOrgChart(
        getAttributePolicy(policies, entry.key, entry)
      )
  );

export const filterCatalogForCardOverlays = <
  T extends {
    key: string;
    internal?: boolean;
    pickerExcluded?: boolean;
    entityTypes?: readonly AttributeEntityType[];
  },
>(
  catalog: readonly T[],
  policies: AttributePolicyByKey,
  entityKind: OverlayCardEntityKind
): T[] =>
  catalog.filter(
    (entry) =>
      overlayEntryForCardEntity(entry, entityKind) &&
      !isAttributeHiddenFromOrgChart(
        getAttributePolicy(policies, entry.key, entry)
      )
  );

export const cardOverlayAllowedKeys = (
  catalog: readonly {
    key: string;
    internal?: boolean;
    pickerExcluded?: boolean;
    entityTypes?: readonly AttributeEntityType[];
  }[],
  policies: AttributePolicyByKey,
  entityKind: OverlayCardEntityKind
): string[] =>
  filterCatalogForCardOverlays(catalog, policies, entityKind).map(
    (entry) => entry.key
  );

export const filterOverlayCatalogByPolicies = <
  T extends { key: string; internal?: boolean },
>(
  catalog: readonly T[],
  policies: AttributePolicyByKey
): T[] =>
  catalog.filter(
    (entry) =>
      !isAttributeHiddenFromOrgChart(
        getAttributePolicy(policies, entry.key, entry)
      )
  );

const catalogEntriesForEntityType = (
  catalog: readonly AttributeCatalogEntry[],
  entityType?: AttributeEntityType
): readonly AttributeCatalogEntry[] =>
  entityType
    ? catalog.filter((entry) => entry.entityTypes.includes(entityType))
    : catalog;

export const catalogHasEnabledOrgChartAttributes = (
  catalog: readonly AttributeCatalogEntry[],
  policies: AttributePolicyByKey,
  entityType?: AttributeEntityType
): boolean =>
  filterOverlayCatalogByPolicies(
    catalogEntriesForEntityType(catalog, entityType),
    policies
  ).length > 0;

export const catalogHasPropertiesPickerAttributes = (
  catalog: readonly AttributeCatalogEntry[],
  policies: AttributePolicyByKey,
  entityType?: AttributeEntityType
): boolean =>
  filterCatalogForPropertiesPicker(
    catalogEntriesForEntityType(catalog, entityType),
    policies
  ).length > 0;

export const enabledOverlayCatalogKeySet = (
  catalog: readonly { key: string; internal?: boolean }[],
  policies: AttributePolicyByKey
): Set<string> =>
  new Set(
    filterOverlayCatalogByPolicies(catalog, policies).map((entry) => entry.key)
  );

export const isOrgChartOverlayFieldEnabled = ({
  key,
  policies,
  overlaysEnabled,
  enabledCatalogKeys,
}: {
  key: string;
  policies: AttributePolicyByKey;
  overlaysEnabled: boolean;
  enabledCatalogKeys?: ReadonlySet<string>;
}): boolean => {
  if (!overlaysEnabled) {
    return true;
  }
  if (isAttributeHiddenFromOrgChart(getAttributePolicy(policies, key))) {
    return false;
  }
  if (enabledCatalogKeys === undefined) {
    return true;
  }
  if (enabledCatalogKeys.size === 0) {
    return false;
  }
  return enabledCatalogKeys.has(key);
};

export const overlayRowAllowsAddingNew = ({
  kind,
  key,
  policy,
}: {
  kind: "bag" | "scalar";
  key: string;
  policy: AttributeDisplayPolicy;
}): boolean => {
  if (policy.kind !== "editable") {
    return false;
  }
  if (
    kind === "bag" ||
    key === "bands" ||
    key === "position.jobTitle" ||
    key === "position.name" ||
    key === "jobTitle" ||
    key === "dailyRate"
  ) {
    return policy.allowAddingNew;
  }
  return true;
};

export const reconcileOverlayAttributeKeys = ({
  selectedKeys,
  allowedKeys,
  policies,
  seedDefaults,
}: {
  selectedKeys: readonly string[];
  allowedKeys: readonly string[];
  policies: AttributePolicyByKey;
  seedDefaults: boolean;
}): string[] => {
  const allowed = new Set(allowedKeys);
  const kept = selectedKeys.filter((key) => allowed.has(key));
  if (kept.length > 0 || !seedDefaults) {
    return kept;
  }
  return allowedKeys.filter((key) => {
    const policy = getAttributePolicy(policies, key);
    return policy.kind !== "hidden" && policy.defaultOnCards;
  });
};

export type OverlayVisibility = {
  chrome: {
    isEnabled: (key: string) => boolean;
    isReadOnly: (key: string) => boolean;
  };
  extras: {
    isHidden: (key: string) => boolean;
    isReadOnly: (key: string) => boolean;
    allowsAddingNew: (args: { kind: "bag" | "scalar"; key: string }) => boolean;
    filterPicker: (
      catalog: readonly AttributeCatalogEntry[]
    ) => AttributeCatalogEntry[];
    filterCards: <
      T extends {
        key: string;
        internal?: boolean;
        pickerExcluded?: boolean;
        entityTypes?: readonly AttributeEntityType[];
      },
    >(
      catalog: readonly T[],
      entityKind: OverlayCardEntityKind
    ) => T[];
  };
};

/**
 * One interface for overlay show/edit. Chrome stays vanilla when overlays
 * are off. Extras, picker, and cards are empty in that case.
 */
export const createOverlayVisibility = ({
  overlaysEnabled,
  catalogReady,
  policies,
  catalog,
}: {
  overlaysEnabled: boolean;
  catalogReady: boolean;
  policies: AttributePolicyByKey;
  catalog: readonly { key: string; internal?: boolean }[];
}): OverlayVisibility => {
  const enabledCatalogKeys =
    overlaysEnabled && catalogReady
      ? enabledOverlayCatalogKeySet(catalog, policies)
      : undefined;

  return {
    chrome: {
      isEnabled: (key) =>
        isOrgChartOverlayFieldEnabled({
          key,
          policies,
          overlaysEnabled,
          ...(enabledCatalogKeys === undefined ? {} : { enabledCatalogKeys }),
        }),
      isReadOnly: (key) =>
        overlaysEnabled &&
        isAttributeReadOnlyInOrgChart(getAttributePolicy(policies, key)),
    },
    extras: {
      isHidden: (key) =>
        !overlaysEnabled ||
        isAttributeHiddenFromOrgChart(getAttributePolicy(policies, key)),
      isReadOnly: (key) =>
        !overlaysEnabled ||
        isAttributeReadOnlyInOrgChart(getAttributePolicy(policies, key)),
      allowsAddingNew: ({ kind, key }) =>
        overlaysEnabled &&
        overlayRowAllowsAddingNew({
          kind,
          key,
          policy: getAttributePolicy(policies, key),
        }),
      filterPicker: (pickerCatalog) =>
        overlaysEnabled
          ? filterCatalogForPropertiesPicker(pickerCatalog, policies)
          : [],
      filterCards: (cardCatalog, entityKind) =>
        overlaysEnabled
          ? filterCatalogForCardOverlays(cardCatalog, policies, entityKind)
          : [],
    },
  };
};

export const detailsOverlayFieldGate = (
  args: Parameters<typeof createOverlayVisibility>[0]
): OverlayVisibility["chrome"] => createOverlayVisibility(args).chrome;
